import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLang } from "../../contexts/LanguageContext";
import { t, tr, LANGS } from "../../i18n/translations";

export default function Header({ onBook }) {
  const { lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    if (!onHome) {
      navigate(`/#${id}`);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled ? "bg-[#FAFAFA]/85 backdrop-blur-xl border-b border-[#E8E1DA]" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="font-serif text-2xl sm:text-3xl tracking-tight text-[#1A1A1A]">
          Abril Suarez<span className="text-[#8B6D5E]">.</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-10">
          <button onClick={() => scrollTo("services")} data-testid="nav-services" className="overline text-[#1A1A1A] link-underline">
            {tr(t.nav.services, lang)}
          </button>
          <button onClick={() => scrollTo("portfolio")} data-testid="nav-portfolio" className="overline text-[#1A1A1A] link-underline">
            {tr(t.nav.portfolio, lang)}
          </button>
          <button onClick={() => scrollTo("courses")} data-testid="nav-courses" className="overline text-[#1A1A1A] link-underline">
            {tr(t.nav.courses, lang)}
          </button>
          <Link to="/quiz" data-testid="nav-quiz" className="overline text-[#8B6D5E] link-underline">
            Quiz
          </Link>
          <Link to="/method" data-testid="nav-method" className="overline text-[#1A1A1A] link-underline">
            Método
          </Link>
          <Link to="/journal" data-testid="nav-journal" className="overline text-[#1A1A1A] link-underline">
            Journal
          </Link>
          <button onClick={() => scrollTo("about")} data-testid="nav-about" className="overline text-[#1A1A1A] link-underline">
            {tr(t.nav.about, lang)}
          </button>
          <button onClick={() => scrollTo("contact")} data-testid="nav-contact" className="overline text-[#1A1A1A] link-underline">
            {tr(t.nav.contact, lang)}
          </button>
        </nav>

        <div className="flex items-center gap-5">
          <div className="hidden sm:flex items-center gap-3" data-testid="lang-switcher">
            {LANGS.map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                data-testid={`lang-${l}`}
                className={`overline transition-colors ${lang === l ? "text-[#8B6D5E]" : "text-[#5C5C5C] hover:text-[#1A1A1A]"}`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
          <button onClick={onBook} data-testid="header-book-btn" className="btn-primary hidden md:inline-flex">
            {tr(t.nav.book, lang)}
          </button>
          <button onClick={() => setOpen((v) => !v)} className="lg:hidden text-[#1A1A1A]" data-testid="menu-toggle" aria-label="Menu">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-[#FAFAFA] border-t border-[#E8E1DA]" data-testid="mobile-menu">
          <div className="max-w-[1400px] mx-auto px-6 py-8 flex flex-col gap-6">
            {[
              ["services", t.nav.services],
              ["portfolio", t.nav.portfolio],
              ["courses", t.nav.courses],
              ["about", t.nav.about],
              ["contact", t.nav.contact],
            ].map(([id, label]) => (
              <button key={id} onClick={() => scrollTo(id)} className="font-serif text-3xl text-left">
                {tr(label, lang)}
              </button>
            ))}
            <Link to="/quiz" onClick={() => setOpen(false)} className="font-serif text-3xl text-left text-[#8B6D5E]" data-testid="mobile-nav-quiz">
              Quiz personalizado
            </Link>
            <div className="flex gap-4 pt-4 border-t border-[#E8E1DA]">
              {LANGS.map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  data-testid={`mobile-lang-${l}`}
                  className={`overline ${lang === l ? "text-[#8B6D5E]" : "text-[#5C5C5C]"}`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            <button onClick={() => { setOpen(false); onBook(); }} className="btn-primary w-full justify-center mt-4" data-testid="mobile-book-btn">
              {tr(t.nav.book, lang)}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
